import { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import requestUtils from 'api/requestUtils'
import isLoyaltyCalculationRequestWithBonus from 'loyalty/lib/pointsCalculation/isLoyaltyCalculationRequestWithBonus'

type Payload = paths['/api/lux-loyalty/points/calculate-earn']['post']['parameters']['body']['payload']
type Response = paths['/api/lux-loyalty/points/calculate-earn']['post']['responses']['200']['content']['application/json']
type PostPurchasePayload = paths['/api/lux-loyalty/points/calculate-earn/post-purchase']['post']['parameters']['body']['payload']

function loyaltyServerProductDetailsMap(options: App.LoyaltyPointsEarnCalculationRequest): Payload['productDetails'] {
  if (isLoyaltyCalculationRequestWithBonus(options)) {
    return {
      loyaltyProductType: options.loyaltyProductType,
      offerId: options.offerId,
      packageId: options.packageId,
      earnKey: options.earnKey,
      promoKey: options.promoKey,
    }
  }

  return {
    loyaltyProductType: options.loyaltyProductType,
    earnKey: options.earnKey,
    promoKey: options.promoKey,
  }
}

interface Options {
  customerId: string
  currency: string
  luxPlusMember: boolean
}

function mapResponseToCalculation(request: App.LoyaltyPointsEarnCalculationRequest, response: Response): App.LoyaltyPointsEarnCalculation {
  const basePoints = response.result.totalBasePoints
  const bonusPoints = response.result.totalBonusPoints
  const bonusStatusCredits = response.result.totalBonusStatusCredits
  const statusCredits = response.result.totalStatusCredits
  const appliedRuleIds = response.result.appliedCustomRuleIds

  return {
    request,
    points: basePoints + bonusPoints,
    basePoints,
    bonusPoints,
    bonusStatusCredits,
    statusCredits,
    appliedRuleIds,
    roundingEnabled: response.result.roundingEnabled,
    roundedPointsEarnRatio: response.result.totalPointsEarnRatio,
  }
}

export async function requestLoyaltyEarnPointsCalculation(request: App.LoyaltyPointsEarnCalculationRequest, options: Options): Promise<App.LoyaltyPointsEarnCalculation> {
  const payload: Payload = {
    publicPrice: request.price,
    luxPlusPrice: request.memberPrice,
    luxPlusMember: options.luxPlusMember,
    currency: options.currency,
    mx: request.mx,
    customerId: options.customerId,
    productDetails: loyaltyServerProductDetailsMap(request),
  }

  const response = await requestUtils.post<Response, Payload>('/api/lux-loyalty/points/calculate-earn',
    payload,
    { credentials: 'include' },
  )

  return mapResponseToCalculation(request, response)
}

interface PostPurchaseOptions extends Options {
  orderItemId?: string
  orderId?: string
}

/**
 * Calculate loyalty points for a post-purchase item update.
 * The backend will look up the original booking date from the order item.
 */
export async function requestLoyaltyEarnPointsCalculationPostPurchase(
  request: App.LoyaltyPointsEarnCalculationRequest,
  options: PostPurchaseOptions,
): Promise<App.LoyaltyPointsEarnCalculation> {
  if (!options.orderItemId || !options.orderId) {
    throw new Error('orderItemId and orderId are required', { cause: { orderItemId: options.orderItemId, orderId: options.orderId } })
  }

  const payload: PostPurchasePayload = {
    publicPrice: request.price,
    luxPlusPrice: request.memberPrice,
    luxPlusMember: options.luxPlusMember,
    currency: options.currency,
    mx: request.mx,
    customerId: options.customerId,
    productDetails: loyaltyServerProductDetailsMap(request),
    itemId: options.orderItemId,
    orderId: options.orderId,
  }

  const response = await requestUtils.post<Response, PostPurchasePayload>('/api/lux-loyalty/points/calculate-earn/post-purchase',
    payload,
    { credentials: 'include' },
  )

  return mapResponseToCalculation(request, response)
}
